/**
 * Admin Billing Settings (Apr 26, 2026)
 *
 * Configure invoice sender + branding details. Powers the invoice PDF
 * (replaces previously hardcoded TitleFlow Services block).
 *
 * Backend: GET / PUT /api/admin/settings/billing
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Receipt,
  Save,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Building2,
  ExternalLink,
  Eye,
} from 'lucide-react';
import { formatDateTimeLocal } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const EMPTY_SETTINGS = {
  company_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  phone: '',
  billing_email: '',
  website: '',
  logo_url: '',
  footer_note: '',
  payment_terms: '',
};

const REQUIRED_FIELDS = [
  'company_name',
  'address_line1',
  'city',
  'state',
  'zip_code',
  'billing_email',
];

const FIELD_LABELS = {
  company_name: 'Company / Sender Name',
  address_line1: 'Address Line 1',
  address_line2: 'Address Line 2',
  city: 'City',
  state: 'State',
  zip_code: 'ZIP Code',
  phone: 'Phone',
  billing_email: 'Billing Email',
  website: 'Website',
  logo_url: 'Logo URL',
  footer_note: 'Invoice Footer Note',
  payment_terms: 'Payment Terms',
};

const formatTimestamp = (iso) => {
  if (!iso) return null;
  try {
    return formatDateTimeLocal(iso);
  } catch {
    return iso;
  }
};

const validate = (v) => {
  const errs = {};
  REQUIRED_FIELDS.forEach((k) => {
    if (!(v[k] || '').trim()) errs[k] = `${FIELD_LABELS[k]} is required`;
  });
  if (v.billing_email) {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!re.test(v.billing_email.trim()))
      errs.billing_email = 'Enter a valid email address';
  }
  return errs;
};

const AdminBillingSettings = () => {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [errors, setErrors] = useState({});
  const [meta, setMeta] = useState({
    is_configured: false,
    updated_at: null,
    updated_by: null,
  });
  
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/admin/settings/billing`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to fetch billing settings');
      const data = await resp.json();
      const next = { ...EMPTY_SETTINGS };
      Object.keys(EMPTY_SETTINGS).forEach((k) => {
        next[k] = data[k] || '';
      });
      setSettings(next);
      setMeta({
        is_configured: !!data.is_configured,
        updated_at: data.updated_at,
        updated_by: data.updated_by,
      });
      setErrors({});
    } catch (err) {
      console.error(err);
      toast.error('Failed to load billing settings');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };
  
  const handleSave = async () => {
    const errs = validate(settings);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error('Please fix the highlighted fields');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      // Strip empty optional fields before sending
      const payload = {};
      Object.keys(settings).forEach((k) => {
        const v = (settings[k] || '').trim();
        if (REQUIRED_FIELDS.includes(k) || v) payload[k] = v;
      });
      
      const resp = await fetch(`${API}/admin/settings/billing`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Save failed');
      }
      const data = await resp.json();
      setMeta({
        is_configured: !!data.is_configured,
        updated_at: data.updated_at,
        updated_by: data.updated_by,
      });
      toast.success('Billing settings saved — invoice PDFs will use these details');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to save billing settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handlePreview = async () => {
    // Apr 26, 2026 — previews UNSAVED form values so admins can iterate
    // on copy/branding before committing. Backend renders a synthetic
    // multi-line invoice with the in-memory settings (no DB write).
    const errs = validate(settings);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error('Please complete required fields before previewing');
      return;
    }
    setPreviewing(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      Object.keys(settings).forEach((k) => {
        const v = (settings[k] || '').trim();
        if (REQUIRED_FIELDS.includes(k) || v) payload[k] = v;
      });
      const resp = await fetch(`${API}/admin/settings/billing/preview-pdf`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Preview failed');
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      // Open the synthetic preview in a new tab for inline review.
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Pop-up blocked → fall back to download
        const a = document.createElement('a');
        a.href = url;
        a.download = 'billing-settings-preview.pdf';
        a.click();
      }
      // Clean up the blob URL once the browser has had a chance to load it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Failed to render preview');
    } finally {
      setPreviewing(false);
    }
  };
  
  const renderField = (key, options = {}) => {
    const { type = 'text', placeholder, multiline, maxLength } = options;
    const required = REQUIRED_FIELDS.includes(key);
    const Component = multiline ? Textarea : Input;
    return (
      <div>
        <Label htmlFor={key} className="text-xs font-semibold text-slate-700">
          {FIELD_LABELS[key]}
          {required && <span className="text-rose-500 ml-1">*</span>}
        </Label>
        <Component
          id={key}
          type={type}
          value={settings[key] || ''}
          onChange={(e) => handleChange(key, e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          rows={multiline ? 3 : undefined}
          className={`mt-1 ${errors[key] ? 'border-rose-400 focus-visible:ring-rose-300' : ''}`}
          data-testid={`billing-settings-${key}`}
        />
        {errors[key] && (
          <p className="mt-1 text-xs text-rose-600">{errors[key]}</p>
        )}
      </div>
    );
  };
  
  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6" data-testid="admin-billing-settings">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center">
            <Receipt className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Billing Settings</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Sender details and branding for invoice PDFs
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchSettings}
          disabled={loading || saving}
          data-testid="billing-settings-refresh"
        >
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      
      {/* Configuration banner */}
      {!loading && !meta.is_configured && (
        <div
          className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
          data-testid="billing-settings-not-configured-banner"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-900">Settings not configured</p>
            <p className="text-amber-800 mt-0.5">
              Invoice PDF generation will be blocked until the company sender
              details below are saved. Required fields are marked with{' '}
              <span className="text-rose-600 font-semibold">*</span>.
            </p>
          </div>
        </div>
      )}
      
      {!loading && meta.is_configured && (
        <div
          className="flex items-start gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-200"
          data-testid="billing-settings-configured-banner"
        >
          <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <p className="font-semibold text-emerald-900">Active configuration</p>
            <p className="text-emerald-800 mt-0.5">
              All invoice PDFs render with these details.
              {meta.updated_at && (
                <>
                  {' '}Last updated{' '}
                  <span className="font-medium">{formatTimestamp(meta.updated_at)}</span>
                  {meta.updated_by && (
                    <>
                      {' '}by <span className="font-medium">{meta.updated_by}</span>
                    </>
                  )}
                  .
                </>
              )}
            </p>
          </div>
        </div>
      )}
      
      {/* Company / sender block */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-4 h-4 text-slate-500" />
            Company / Sender
          </CardTitle>
          <CardDescription>
            Appears in the top-left of every invoice PDF.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderField('company_name', { placeholder: 'PNA Title Services LLC' })}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('address_line1', { placeholder: '1234 Greenwich Street' })}
            {renderField('address_line2', { placeholder: 'Suite 200 (optional)' })}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {renderField('city', { placeholder: 'Wilmington' })}
            {renderField('state', { placeholder: 'DE' })}
            {renderField('zip_code', { placeholder: '19801' })}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('phone', { placeholder: '(302) 555-0150' })}
            {renderField('billing_email', {
              type: 'email',
              placeholder: 'billing@yourcompany.com',
            })}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderField('website', { placeholder: 'www.yourcompany.com' })}
            {renderField('logo_url', {
              placeholder: 'https://… (optional)',
              type: 'url',
            })}
          </div>
          
          {settings.logo_url && (
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <ExternalLink className="w-3 h-3" />
              Logo will be loaded from this URL on every PDF render.
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Footer / payment instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Payment & Footer</CardTitle>
          <CardDescription>
            Optional payment-terms blurb and footer note rendered below the totals.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {renderField('payment_terms', {
            multiline: true,
            placeholder: 'e.g. Net 30 — Payment due within 30 days of invoice date.',
            maxLength: 500,
          })}
          {renderField('footer_note', {
            multiline: true,
            placeholder:
              'e.g. Please remit payment via ACH to the account number on file. Thank you for your business.',
            maxLength: 2000,
          })}
        </CardContent>
      </Card>
      
      {/* Save bar */}
      <div className="sticky bottom-0 z-10 bg-white/95 backdrop-blur border-t border-slate-200 -mx-6 px-6 py-3 flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500 hidden md:block">
          Tip: click <span className="font-semibold text-slate-700">Preview PDF</span> to see how a sample invoice will render before saving.
        </p>
        <div className="flex items-center gap-2 ml-auto">
          <Button
            variant="outline"
            onClick={handlePreview}
            disabled={previewing || saving || loading}
            data-testid="billing-settings-preview"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            {previewing ? 'Rendering…' : 'Preview PDF'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="billing-settings-save"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving…' : 'Save Billing Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminBillingSettings;
